'use client';

import { ReactNode, createContext, useContext, useEffect, useRef } from 'react';

import { css } from 'styled-system/css';

import { useInView } from '@/hooks/use-in-view';

import { useIsProductSlideActive } from './product-slide.client';

const InViewContext = createContext(false);

const frameClass = css({ position: 'relative', w: 'full', h: 'full', overflow: 'hidden' });

/**
 * The media container of every product media (same box + in-view target as the originals):
 * observes itself with `useInView` (200px rootMargin) and tells the nested `ProductMediaVideo`
 * when to mount. The poster image and overlay stay server children.
 */
export const ProductMediaFrame = ({ children }: { children: ReactNode }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isInView = useInView(containerRef);

  return (
    <div ref={containerRef} className={frameClass}>
      <InViewContext value={isInView}>{children}</InViewContext>
    </div>
  );
};

type VideoSource = { src: string; media?: string };

type ProductMediaVideoProps = {
  poster: string;
  sources: VideoSource[];
  className: string;
  isActive?: boolean;
};

/**
 * The lazily mounted `<video>` of a product media: exists only once the frame is near the
 * viewport (no file fetched during initial load), then stays mounted (no re-download on revisit);
 * plays while its slide is active, pauses otherwise.
 */
export const ProductMediaVideo = ({ poster, sources, className, isActive }: ProductMediaVideoProps) => {
  const isInView = useContext(InViewContext);
  const active = useIsProductSlideActive(isActive);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // isInView gates the video's existence, so the effect must re-run when it mounts.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isInView) return;
    if (active) video.play().catch(() => {});
    else video.pause();
  }, [active, isInView]);

  if (!isInView) return null;

  return (
    <video ref={videoRef} className={className} autoPlay muted loop playsInline preload="metadata" poster={poster}>
      {sources.map((source) => (
        <source key={source.src} src={source.src} type="video/mp4" media={source.media} />
      ))}
    </video>
  );
};
